<template>
  <div>
    <v-text-field
      v-model="search"
      label="Buscar CID por código ou descrição, ou digite a doença..."
      prepend-inner-icon="mdi-magnify"
      outlined
      dense
      clearable
      :loading="searching"
      @input="onSearchInput"
    ></v-text-field>

    <!-- Resultados da busca -->
    <div
      v-if="results.length > 0"
      class="cid-results mb-3 pa-3 rounded-lg"
      style="background: #f5f5f5"
    >
      <p class="caption grey--text text--darken-1 mb-2 font-weight-medium">
        Resultados — clique para adicionar:
      </p>
      <v-chip
        v-for="(cid, index) in results"
        :key="index"
        class="ma-1"
        outlined
        :color="cid.type === 'categoria' ? 'teal darken-2' : 'cyan darken-1'"
        small
        :disabled="isSelected(cid)"
        @click="add(cid)"
      >
        <v-icon left x-small>
          {{ isSelected(cid) ? "mdi-check" : "mdi-plus" }}
        </v-icon>
        <strong class="mr-1">{{ cid.code }}</strong>
        {{ cid.description }}
      </v-chip>
    </div>
    <p
      v-else-if="search && search.length >= 2 && !searching"
      class="caption grey--text mb-2"
    >
      <v-icon small color="grey" class="mr-1">mdi-alert-circle-outline</v-icon>
      Nenhum CID encontrado para "{{ search }}".
    </p>
    <p
      v-else-if="search && search.length < 2 && !searching"
      class="caption grey--text"
    >
      Digite ao menos 2 caracteres para buscar
    </p>

    <!--
      Registrar doença sem CID. Fica FORA do ramo "nenhum encontrado" de
      propósito: quem já sabe que a doença não tem código não deveria precisar
      esperar a busca falhar. Aparece assim que há texto suficiente, tanto com
      resultados na tela quanto sem.
    -->
    <v-btn
      v-if="podeAdicionarSemCodigo"
      small
      outlined
      color="catTeal"
      class="mb-3"
      @click="adicionarSemCodigo"
    >
      <v-icon left small>mdi-plus</v-icon>
      {{ rotuloSemCodigo }}
    </v-btn>

    <!-- CIDs selecionados — escolha a categoria de cada um -->
    <div
      v-if="model.length > 0"
      class="mt-3 pa-3 rounded-lg"
      style="background: #ecfeff"
    >
      <p class="caption font-weight-medium mb-2" style="color: #0e7490">
        <v-icon small color="catTeal" class="mr-1">mdi-check-circle</v-icon>
        Selecionados — defina a categoria de cada doença:
      </p>
      <div
        v-for="(cid, index) in model"
        :key="'sel-' + index"
        class="selected-row"
      >
        <div class="sel-cid">
          <strong v-if="cid.code" class="mr-1">{{ cid.code }}</strong>
          <span>{{ cid.description }}</span>
          <span v-if="!cid.code" class="caption grey--text ml-1">
            (sem CID)
          </span>
        </div>
        <v-select
          v-model="cid.category"
          :items="categoryOptions"
          :disabled="!!fixedCategory"
          dense
          outlined
          hide-details
          class="sel-cat"
          @change="emitChange"
        ></v-select>
        <v-btn icon small color="catTeal" @click="remove(index)">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>
    <v-alert
      v-if="model.length === 0"
      type="info"
      dense
      text
      class="mt-3 rounded-lg"
      icon="mdi-information-outline"
      color="catTeal"
    >
      Pesquise um CID ou registre uma doença sem código
    </v-alert>
  </div>
</template>

<script>
import { searchCids } from "@/services/cidService";

export default {
  name: "CidField",

  props: {
    // Array de objetos de CID já selecionados
    value: { type: Array, default: () => [] },

    // salvia-770: quando o campo vive num card de assunto específico (Doença
    // crônica, Transtorno), a categoria não é escolha do médico — ela vem do
    // card. O seletor continua visível, preenchido e desabilitado, para ele
    // ver em que categoria o registro vai entrar.
    fixedCategory: { type: String, default: null },

    // Itens JÁ salvos no paciente. Sem isto a checagem de duplicata só olharia
    // o que está sendo adicionado agora, e a mesma doença entraria de novo a
    // cada vez que o diálogo fosse reaberto.
    existing: { type: Array, default: () => [] },
  },

  data() {
    return {
      search: "",
      results: [],
      searching: false,
      debounceTimer: null,
      categoryOptions: [
        { text: "CID (geral)", value: "cid" },
        { text: "Doença crônica", value: "chronic" },
        { text: "Transtorno psicológico", value: "psychological" },
        { text: "Outra doença (sem CID)", value: "other" },
      ],
    };
  },

  computed: {
    model: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

    /** Texto digitado, limpo — base das duas computadas abaixo. */
    termoDigitado() {
      return String(this.search || "").trim();
    },

    /**
     * Some enquanto a busca corre, para o botão não piscar entre um resultado
     * e outro, e some quando a doença digitada já está na lista.
     */
    podeAdicionarSemCodigo() {
      if (this.termoDigitado.length < 2 || this.searching) return false;

      return !this.isSelected({ code: null, description: this.termoDigitado });
    },

    /**
     * Com resultados na tela, o botão é a saída de quem olhou a lista e não
     * achou o que queria; sem resultados, é o único caminho. O texto muda para
     * não competir com os resultados quando eles existem.
     */
    rotuloSemCodigo() {
      const termo = this.termoDigitado;

      return this.results.length > 0
        ? `Nenhum destes? Adicionar "${termo}" sem CID`
        : `Adicionar "${termo}" como outra doença`;
    },
  },

  beforeDestroy() {
    clearTimeout(this.debounceTimer);
  },

  methods: {
    onSearchInput() {
      clearTimeout(this.debounceTimer);
      if (!this.search || this.search.length < 2) {
        this.results = [];
        return;
      }
      this.debounceTimer = setTimeout(() => this.runSearch(), 400);
    },

    async runSearch() {
      this.searching = true;
      try {
        this.results = await searchCids(this.search);
      } catch (err) {
        console.error("Erro ao buscar CID:", err);
        if (err.response?.status === 422) {
          console.error("Validação falhou:", err.response.data);
        }
        this.results = [];
      } finally {
        this.searching = false;
      }
    },

    isSelected(cid) {
      // Compara por código quando há um. Sem código, cai na descrição: senão
      // dois itens de `code: null` (doenças sem CID) casariam entre si, e a
      // segunda doença seria recusada como duplicada.
      // salvia-770: `model` e o que esta sendo adicionado agora; `existing` e o
      // que ja esta gravado no paciente. Sem os dois, a mesma doenca entraria
      // de novo a cada reabertura do dialogo.
      const todos = [...this.model, ...this.existing];

      if (cid.code) return todos.some((c) => c.code === cid.code);

      const alvo = String(cid.description || "").trim().toLowerCase();
      return todos.some(
        (c) =>
          !c.code &&
          String(c.description || "").trim().toLowerCase() === alvo,
      );
    },

    /**
     * Registra doença que ainda não tem CID no catálogo.
     *
     * Nada mudou no backend: `patient_conditions.code` já é nullable, a
     * validação já aceita `category: other` e o controller já grava
     * `source = 'manual'` quando o código vem vazio. Faltava só o caminho na
     * tela — até aqui, "Nenhum CID encontrado" era um beco sem saída.
     */
    adicionarSemCodigo() {
      const descricao = this.termoDigitado;
      if (!descricao) return;

      const novo = {
        code: null,
        description: descricao,
        category: this.fixedCategory || "other",
      };
      if (this.isSelected(novo)) return;

      this.$emit("input", [...this.model, novo]);

      // Cancela a busca agendada antes de limpar o campo: sem isto, um timer
      // de 400ms ainda pendente dispararia runSearch() com o termo já vazio.
      clearTimeout(this.debounceTimer);
      this.search = "";
      this.results = [];
    },

    add(cid) {
      if (!this.isSelected(cid)) {
        // Sem categoria fixa, entra como "cid" (geral) e o médico ajusta no
        // seletor. Com categoria fixa (card de assunto), já entra na dela.
        this.$emit("input", [
          ...this.model,
          { ...cid, category: this.fixedCategory || "cid" },
        ]);
      }
    },

    remove(index) {
      const next = this.model.slice();
      next.splice(index, 1);
      this.$emit("input", next);
    },

    // Reemite o array após mudar a categoria de um item (garante reatividade).
    emitChange() {
      this.$emit("input", [...this.model]);
    },
  },
};
</script>

<style scoped>
.cid-results {
  max-height: 220px;
  overflow-y: auto;
}

.selected-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  border-bottom: 1px solid #cffafe;
}
.selected-row:last-child {
  border-bottom: none;
}
.sel-cid {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12.5px;
  color: var(--ds-cat-teal);
  line-height: 1.3;
}
.sel-cat {
  flex: 0 0 190px;
  max-width: 190px;
}
</style>
